// Превью одного pending-вложения: кадр/иконка kind, прогресс загрузки, текст
// ошибки, кнопка «убрать». Чистый презентер без drag-and-drop — им пользуются и
// сортируемая очередь вложений (`AttachmentPreview`), и роль-слоты кадров
// (`FrameRoleSlots`), поэтому dnd-обвязка живёт у вызывающего.

import { useRef, useState } from "react";
import { Loader2, Music, Video, X } from "lucide-react";

import type { PendingUpload } from "~/shared/store/types";
import { AudioPlayTile } from "~/shared/ui/audio-play-tile";

// Круговой прогресс через SVG: фоновое серое кольцо + цветная дуга
// поверх. stroke-dasharray + dashoffset = (1 - p) * circumference.
// Выбран SVG (не canvas), чтобы шкалировался под любой размер контейнера.
export function CircularProgress({ percent }: { percent: number }) {
    const size = 28;
    const stroke = 3;
    const radius = (size - stroke) / 2;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (Math.max(0, Math.min(100, percent)) / 100) * circumference;
    return (
        <div className="relative flex items-center justify-center">
            <svg width={size} height={size} className="-rotate-90">
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    fill="none"
                    stroke="rgba(255,255,255,0.2)"
                    strokeWidth={stroke}
                />
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    fill="none"
                    stroke="white"
                    strokeWidth={stroke}
                    strokeLinecap="round"
                    strokeDasharray={circumference}
                    strokeDashoffset={offset}
                    style={{ transition: "stroke-dashoffset 80ms linear" }}
                />
            </svg>
            <span className="absolute text-[9px] font-semibold text-white tabular-nums">
                {Math.round(percent)}
            </span>
        </div>
    );
}

// `#t=0.1` — Media Fragment URI: браузер seek'ает на 0.1s и показывает
// первый кадр без autoplay. Если загрузка metadata падает (медленная сеть,
// CORS, повреждённый файл) — `onError` переключает на иконку Video.
export function VideoFrameOrFallback({ src, iconClassName }: { src: string; iconClassName: string }) {
    const [failed, setFailed] = useState(false);
    if (failed) {
        return (
            <div className="flex h-full w-full items-center justify-center bg-zinc-900 text-violet-300">
                <Video className={iconClassName} />
            </div>
        );
    }
    return (
        <video
            src={`${src}#t=0.1`}
            preload="metadata"
            muted
            playsInline
            className="h-full w-full object-cover"
            onError={() => setFailed(true)}
        />
    );
}

// Перетаскивание очереди вложений начинается со смещения в 4 px (`PointerSensor`
// в `AttachmentPreview`). Жест, ушедший дальше, — это перенос плитки, а не
// запрос увеличения, поэтому лайтбокс по нему не открывается.
const DRAG_SLOP_PX = 4;

/**
 * Имя файла подписью под плиткой. В `title` оно тоже остаётся, но нативного
 * тултипа на тач-экране нет — а по миниатюре 16×16 два похожих кадра не
 * различить. Ширину подписи задаёт колонка caller'а, как и размер самой плитки.
 */
export function AttachmentThumbCaption({ item }: { item: PendingUpload }) {
    return (
        <span className="line-clamp-1 text-center text-[11px] break-all text-zinc-400">{item.fileName}</span>
    );
}

export interface AttachmentThumbBodyProps {
    item: PendingUpload;
    onRemove: (localId: string) => void;
    removeLabel?: string;
    /** Подписи кнопки прослушивания звука; локали — у caller'а. */
    playLabel?: string;
    pauseLabel?: string;
    /**
     * Открыть вложение крупно. Не задан — увеличения у плитки нет (так ведут
     * себя места, где ленты для лайтбокса не собирается). Звук сюда не попадает:
     * увеличивать у него нечего, он и так играет прямо в плитке.
     */
    onZoom?: (localId: string) => void;
    /** aria-label кнопки увеличения; обязателен, когда задан `onZoom`. */
    zoomLabel?: string;
}

/** Внутренности миниатюры без внешнего контейнера — рамку/размер задаёт caller. */
export function AttachmentThumbBody({
    item,
    onRemove,
    removeLabel,
    playLabel,
    pauseLabel,
    onZoom,
    zoomLabel,
}: AttachmentThumbBodyProps) {
    const isError = item.status === "error";
    const isLoading = item.status === "presigning" || item.status === "uploading";
    // Увеличивать есть что только у кадра: у звука вместо кадра плеер, а без
    // ссылки на превью показана иконка kind — открывать нечего.
    const canZoom =
        onZoom !== undefined &&
        item.previewUrl !== undefined &&
        (item.kind === "image" || item.kind === "video");
    // Координаты начала жеста: клик отличается от перетаскивания только тем,
    // насколько уехал указатель. Событие не глушим — иначе плитка перестала бы
    // перетаскиваться за собственный кадр.
    const gestureStart = useRef<{ x: number; y: number } | null>(null);

    return (
        <>
            {item.previewUrl && item.kind === "image" ? (
                <img
                    src={item.previewUrl}
                    alt={item.fileName}
                    className="h-full w-full object-cover"
                    draggable={false}
                />
            ) : item.previewUrl && item.kind === "video" ? (
                <VideoFrameOrFallback src={item.previewUrl} iconClassName="h-7 w-7" />
            ) : item.previewUrl && item.kind === "audio" ? (
                // Звук слушается прямо в очереди: до отправки это единственный
                // способ убедиться, что прикреплена нужная дорожка.
                <AudioPlayTile
                    src={item.previewUrl}
                    size="sm"
                    label={{
                        play: playLabel ?? "Play",
                        pause: pauseLabel ?? "Pause",
                    }}
                />
            ) : (
                // Fallback: файл ещё без ссылки на превью — показываем иконку kind.
                // Имя файла — в title-атрибуте wrapper'а (нативный tooltip при hover).
                <div className="flex h-full w-full items-center justify-center bg-zinc-900 text-violet-300">
                    {item.kind === "video" ? (
                        <Video className="h-7 w-7" />
                    ) : item.kind === "audio" ? (
                        <Music className="h-7 w-7" />
                    ) : null}
                </div>
            )}

            {isLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                    {item.status === "uploading" ? (
                        <CircularProgress percent={item.progress} />
                    ) : (
                        // presigning — % ещё не определён, оставляем спиннер
                        <Loader2 className="h-4 w-4 animate-spin text-white" />
                    )}
                </div>
            )}

            {isError && (
                <div className="absolute inset-x-0 bottom-0 truncate bg-red-600/80 px-1 py-0.5 text-[10px] text-white">
                    {item.error ?? "error"}
                </div>
            )}

            {canZoom && (
                <button
                    type="button"
                    onPointerDown={(e) => {
                        gestureStart.current = { x: e.clientX, y: e.clientY };
                    }}
                    onClick={(e) => {
                        e.stopPropagation();
                        const start = gestureStart.current;
                        gestureStart.current = null;
                        if (start && Math.hypot(e.clientX - start.x, e.clientY - start.y) > DRAG_SLOP_PX) {
                            return;
                        }
                        onZoom(item.localId);
                    }}
                    aria-label={zoomLabel ?? "Enlarge"}
                    className="absolute inset-0 cursor-zoom-in bg-transparent transition-colors hover:bg-black/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400"
                />
            )}

            <button
                type="button"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                    e.stopPropagation();
                    onRemove(item.localId);
                }}
                className="absolute top-1 right-1 flex h-5 w-5 items-center justify-center rounded-full bg-black/70 text-zinc-200 opacity-0 transition-opacity group-hover:opacity-100 hover:text-white"
                aria-label={removeLabel ?? "Remove attachment"}
            >
                <X className="h-3 w-3" />
            </button>
        </>
    );
}
